// src/hooks/useGoogleMaps.js
import { useState, useEffect, useRef, useCallback } from "react";
import supabase from "../SupaCredentials";

// Variable global para evitar cargas múltiples del script
let googleMapsApiLoadingPromise = null;
let googleMapsApiLoaded = false;

// Constantes (podrían venir de .env o configuración)
const Maps_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// CENTRO DE COBERTURA (Barranquilla Centro)
const COVERAGE_CENTER = { lat: 10.975072170381452, lng: -74.78846869365523 };

// DISTANCIA PARA ZONA PRIMARIA EN KM
const PRIMARY_ZONE_KM = 2;
const ZONE_2_KM = 5.5; // Nueva constante

// --- Función para cargar la API de Google Maps ---
const loadGoogleMapsApi = (apiKey) => {
  if (googleMapsApiLoaded) {
    console.log("useGoogleMaps: API ya cargada.");
    return Promise.resolve();
  }
  if (googleMapsApiLoadingPromise) {
    console.log("useGoogleMaps: API cargando...");
    return googleMapsApiLoadingPromise;
  }

  console.log("useGoogleMaps: Iniciando carga de Google Maps API...");
  googleMapsApiLoadingPromise = new Promise((resolve, reject) => {
    // Evitar añadir script si ya existe (manejo de concurrencia o HMR)
    if (
      document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]')
    ) {
      console.log(
        "useGoogleMaps: Script ya existe, esperando inicialización..."
      );
      // Función para verificar periódicamente si google.maps está disponible
      const checkInterval = setInterval(() => {
        if (window.google && window.google.maps) {
          clearInterval(checkInterval);
          console.log("useGoogleMaps: API inicializada (script existente).");
          googleMapsApiLoaded = true;
          resolve();
        }
      }, 100);
      return;
    }

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places,geometry&callback=googleMapsApiInitialized`; // Añadido callback
    script.async = true;
    script.defer = true;

    window.googleMapsApiInitialized = () => {
      console.log("useGoogleMaps: Google Maps API cargada y lista.");
      googleMapsApiLoaded = true;
      googleMapsApiLoadingPromise = null; // Resetear promesa
      delete window.googleMapsApiInitialized; // Limpiar callback global
      resolve();
    };

    script.onerror = (error) => {
      console.error("useGoogleMaps: Error al cargar Google Maps API:", error);
      googleMapsApiLoadingPromise = null; // Resetear promesa
      delete window.googleMapsApiInitialized; // Limpiar callback global
      reject(error);
    };

    document.head.appendChild(script);
  });

  return googleMapsApiLoadingPromise;
};

// --- Función Helper para crear polígonos ---
const createPolygon = (polygonData) => {
  if (!window.google || !window.google.maps || !polygonData) return null;

  try {
    // Formato GeoJSON (simplificado)
    if (polygonData.features?.[0]?.geometry?.coordinates?.[0]) {
      const coords = polygonData.features[0].geometry.coordinates[0].map(
        (coord) => new window.google.maps.LatLng(coord[1], coord[0])
      );
      return new window.google.maps.Polygon({ paths: coords });
    }
    // Formato Array de {lat, lng}
    else if (Array.isArray(polygonData) && polygonData.length > 0) {
      const paths = polygonData.map((point) => ({
        lat: Number(point.lat),
        lng: Number(point.lng),
      }));
      return new window.google.maps.Polygon({ paths });
    }
    console.warn(
      "useGoogleMaps: Formato de polygonData no reconocido o vacío."
    );
    return null;
  } catch (error) {
    console.error("useGoogleMaps: Error creando polígono:", error);
    return null;
  }
};

// --- Hook Principal ---
export function useGoogleMaps(
  polygonData,
  extendedPolygonData, // Este parámetro se mantiene para compatibilidad pero no se usa
  shouldInitialize = true
) {
  const [isApiLoaded, setIsApiLoaded] = useState(googleMapsApiLoaded);
  const [isInitialized, setIsInitialized] = useState(false); // Indica si los servicios/polígonos están listos

  // Refs para los servicios y polígono de límite
  const autocompleteServiceRef = useRef(null);
  const placesServiceRef = useRef(null);
  const perimeterPolygonRef = useRef(null); // Solo un polígono de límite máximo
  const placesDivRef = useRef(null); // Div necesario para PlacesService

  // --- Efecto para Cargar la API ---
  useEffect(() => {
    if (shouldInitialize && !googleMapsApiLoaded) {
      loadGoogleMapsApi(Maps_API_KEY)
        .then(() => setIsApiLoaded(true))
        .catch(console.error);
    } else if (googleMapsApiLoaded) {
      setIsApiLoaded(true); // Asegurarse de que el estado local refleje la carga global
    }
  }, [shouldInitialize]); // Solo depende de shouldInitialize

  // --- Efecto para Inicializar Servicios y Polígonos una vez cargada la API ---
  useEffect(() => {
    if (isApiLoaded && shouldInitialize && !isInitialized) {
      console.log(
        "useGoogleMaps: API cargada, inicializando servicios y polígono..."
      );
      try {
        if (
          !window.google ||
          !window.google.maps ||
          !window.google.maps.places
        ) {
          throw new Error("Namespace de Google Maps o Places no disponible.");
        }

        // Inicializar Servicios
        autocompleteServiceRef.current =
          new window.google.maps.places.AutocompleteService();

        // PlacesService necesita un elemento DOM (puede estar oculto)
        if (!placesDivRef.current) {
          placesDivRef.current = document.createElement("div");
          placesDivRef.current.style.display = "none";
          document.body.appendChild(placesDivRef.current);
        }
        placesServiceRef.current = new window.google.maps.places.PlacesService(
          placesDivRef.current
        );

        // Crear Polígono de Perímetro (usar polygonData como límite máximo)
        perimeterPolygonRef.current = createPolygon(polygonData);

        console.log(
          "useGoogleMaps: Servicios y polígono de perímetro inicializados."
        );
        setIsInitialized(true); // Marcar como inicializado
      } catch (error) {
        console.error(
          "useGoogleMaps: Error al inicializar servicios/polígono:",
          error
        );
        setIsInitialized(false); // Marcar como no inicializado en caso de error
      }
    }

    // Limpieza del div de PlacesService cuando el hook se desmonte o no se necesite
    return () => {
      if (placesDivRef.current && placesDivRef.current.parentNode) {
        // Podrías querer removerlo sólo si ya no hay otros hooks usándolo,
        // pero para simplificar, lo removemos al desmontar esta instancia.
        // Si tienes múltiples instancias, considera un manejo más global.
        // document.body.removeChild(placesDivRef.current);
        // placesDivRef.current = null;
        // console.log("useGoogleMaps: Div de PlacesService limpiado.");
      }
    };
  }, [isApiLoaded, shouldInitialize, polygonData, isInitialized]);

  // --- NUEVA FUNCIÓN DE VALIDACIÓN ---
  const validateLocation = useCallback(
    (coordinates) => {
      if (
        !isInitialized ||
        !coordinates ||
        !window.google?.maps?.geometry?.poly ||
        !window.google?.maps?.geometry?.spherical
      ) {
        console.warn(
          "useGoogleMaps: No listo para validar o coordenadas inválidas."
        );
        return {
          isInPrimary: false,
          isInExtended: false,
          isValid: false,
          zone: null,
          distanceKm: null,
        };
      }

      try {
        const userPoint = new window.google.maps.LatLng(
          coordinates.lat,
          coordinates.lng
        );

        // PASO 1: Calcular distancia desde el centro PRIMERO
        const centerPoint = new window.google.maps.LatLng(
          COVERAGE_CENTER.lat,
          COVERAGE_CENTER.lng
        );

        const distanceInMeters =
          window.google.maps.geometry.spherical.computeDistanceBetween(
            centerPoint,
            userPoint
          );

        const distanceKm = distanceInMeters / 1000;

        // PASO 2: Verificar si está dentro del perímetro máximo
        const isInsidePerimeter = perimeterPolygonRef.current
          ? window.google.maps.geometry.poly.containsLocation(
              userPoint,
              perimeterPolygonRef.current
            )
          : false;

        // Si no está dentro del perímetro, no hay cobertura
        if (!isInsidePerimeter) {
          console.log(
            `useGoogleMaps: Fuera del perímetro. Distancia: ${distanceKm.toFixed(
              2
            )}km`
          );
          return {
            isInPrimary: false,
            isInExtended: false,
            isValid: false, // NO es válido si está fuera del perímetro
            zone: null,
            distanceKm: Math.round(distanceKm * 100) / 100,
            reason: "outside_coverage",
          };
        }

        // PASO 3: Si está dentro del perímetro, determinar zona según distancia
        let zone;
        let isInPrimary = false;
        let isInExtended = false;

        if (distanceKm <= 2) {
          zone = 1;
          isInPrimary = true;
        } else if (distanceKm <= 5.5) {
          zone = 2;
          isInExtended = true;
        } else {
          zone = 3;
          isInExtended = true;
        }

        console.log(
          `useGoogleMaps: Dentro del perímetro. Distancia: ${distanceKm.toFixed(
            2
          )}km - Zona: ${zone}`
        );

        return {
          isInPrimary,
          isInExtended,
          isValid: true, // Válido porque está dentro del perímetro
          zone, // 1, 2, o 3
          distanceKm: Math.round(distanceKm * 100) / 100,
          reason: zone === 1 ? "primary_zone" : "extended_zone",
        };
      } catch (error) {
        console.error("useGoogleMaps: Error en validateLocation:", error);
        return {
          isInPrimary: false,
          isInExtended: false,
          isValid: false,
          zone: null,
          distanceKm: null,
          reason: "validation_error",
        };
      }
    },
    [isInitialized]
  );

  // --- Función para subir imagen a Supabase ---
  const uploadMapImageToSupabase = useCallback(
    async (imageBlob, coordinates) => {
      if (!imageBlob || !coordinates) return null;

      try {
        // Crear un nombre único para la imagen basado en las coordenadas
        const fileName = `address_${coordinates.lat}_${
          coordinates.lng
        }_${Date.now()}.jpg`;

        // Subir el blob al bucket "addresses_captures"
        const { data, error } = await supabase.storage
          .from("addresses_captures")
          .upload(fileName, imageBlob, {
            contentType: "image/jpeg",
            upsert: true,
          });

        if (error) throw error;

        // Obtener la URL pública
        const { data: publicURLData } = supabase.storage
          .from("addresses_captures")
          .getPublicUrl(fileName);

        return publicURLData.publicUrl;
      } catch (error) {
        console.error("Error uploading map image to Supabase:", error);
        return null;
      }
    },
    []
  );

  // --- Función para obtener imagen estática del mapa ---
  const fetchStaticMapImage = useCallback(
    async (coordinates, uploadToStorage = false) => {
      if (!coordinates || !coordinates.lat || !coordinates.lng) {
        console.error(
          "useGoogleMaps: Coordenadas inválidas para imagen de mapa."
        );
        return null;
      }
      try {
        const zoom = 16;
        const size = "400x300";
        const scale = 2;
        const mapType = "roadmap";
        const url = `https://maps.googleapis.com/maps/api/staticmap?center=${coordinates.lat},${coordinates.lng}&zoom=${zoom}&size=${size}&scale=${scale}&maptype=${mapType}&markers=color:red%7C${coordinates.lat},${coordinates.lng}&key=${Maps_API_KEY}`;

        const response = await fetch(url);
        if (!response.ok)
          throw new Error(`HTTP error! status: ${response.status}`);

        const blob = await response.blob();

        // Si se solicita, subir a Supabase y devolver la URL
        if (uploadToStorage) {
          const imageUrl = await uploadMapImageToSupabase(blob, coordinates);
          return { blob, imageUrl };
        }

        // Si no, simplemente devolver el blob como antes
        return { blob };
      } catch (error) {
        console.error("useGoogleMaps: Error fetching static map image:", error);
        return null;
      }
    },
    [uploadMapImageToSupabase]
  );

  return {
    isMapsApiLoaded: isApiLoaded,
    isMapsInitialized: isInitialized,
    autocompleteService: autocompleteServiceRef.current, // Exponer la instancia actual
    placesService: placesServiceRef.current, // Exponer la instancia actual
    // Polígono de perímetro si se necesita acceso directo:
    // perimeterPolygon: perimeterPolygonRef.current,
    validateLocation, // Función principal de validación
    fetchStaticMapImage, // Función para obtener imagen del mapa
    uploadMapImageToSupabase, // Función para subir a Supabase
    // Datos de configuración (por si se necesitan en otros lugares)
    coverageCenter: COVERAGE_CENTER,
    primaryZoneKm: PRIMARY_ZONE_KM,
  };
}
